// src/components/organisms/integrations/salesforce/create/blocks/automation-project-block.tsx
import { Currency } from 'module/api/account/types';
import {
  SalesforceActions,
  SalesforceIntegrationId,
  RLProjectFieldMapping,
  ProjectFinancialsContractType,
} from 'module/api/integrations/salesforce/types';
import { Loading } from 'module/ui/components/atoms/loading';
import {
  useSalesForceActionFieldsQuery,
  useSalesForceTriggerObjectsQuery,
  useTemplatePlaceholders,
  useSalesForceFinancialsFieldsQuery,
} from 'queries/salesforce';
import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BlockList } from 'components/organisms/signal/detail/block-list/list';
import { ActionDrawer } from '../action-drawer';
import { useAutomationContext } from '../automation-context';
import { getFields } from './shared/block-utils';
import { RenderBlockDetails } from './shared/render-block-details';

export const AutomationProjectBlock: FC = () => {
  const { integrationId } = useParams<SalesforceIntegrationId>();
  const { automationDetails, setAutomationDetails } = useAutomationContext();
  const [actionDrawerVisible, setActionDrawerVisible] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(
    null,
  );
  const [billingMethod, setBillingMethod] =
    useState<ProjectFinancialsContractType | null>(null);
  const [currency, setCurrency] = useState<
    Currency['currencyCode'] | undefined
  >(undefined);

  const salesForceType = SalesforceActions.Project;

  const fieldsKey = getFields(salesForceType);

  const {
    salesforceObject: selectedSalesforceObject,
    salesForceType: selectedSalesForceType,
  } = automationDetails || {};

  const { value } = selectedSalesforceObject || {};

  const { data: salesforceObject } = useSalesForceTriggerObjectsQuery(
    parseInt(integrationId || '0'),
    {},
  );

  const { data: projectFields = [], isLoading, isFetching } =
    useSalesForceActionFieldsQuery(
      parseInt(integrationId || '0'),
      String(value) || salesforceObject?.[0]?.id || '',
      selectedSalesForceType ?? SalesforceActions.Project,
      undefined,
      {
        enabled: !!value || !!salesforceObject?.[0]?.id,
      },
    );

  // Fetch placeholder fields when template is selected
  const { data: placeholderFields, status: placeholderStatus } =
    useTemplatePlaceholders(
      Number(integrationId),
      selectedSalesforceObject?.value.toString() || '',
      currentTemplateId,
      {
        enabled: !!currentTemplateId,
      },
    );

  // Fetch financial fields when billing method and currency are selected
  const { data: financialFields, isFetching: isFinancialsFieldsFetching } =
    useSalesForceFinancialsFieldsQuery(
      Number(integrationId),
      selectedSalesforceObject?.value.toString() || '',
      billingMethod!,
      currency!,
      {
        enabled: billingMethod != null && currency != null,
      },
    );

  const onSave = (updatedFields: RLProjectFieldMapping) => {
    const fields = Object.keys(updatedFields).reduce((acc, key) => {
      acc[key] =
        key !== 'teamMembers'
          ? updatedFields[key] != null
            ? {
              ...updatedFields[key],
              label:
                updatedFields[key]?.valueMeta?.fieldLabel ??
                updatedFields[key]?.label,
            }
            : undefined
          : updatedFields[key];

      return acc;
    }, {});

    setAutomationDetails({
      [fieldsKey]: fields,
    });
  };

  const handleTemplateChange = (templateId: string | null) => {
    setCurrentTemplateId(templateId);
  };

  const handleBillingMethodChange = (
    billingMethodValue: ProjectFinancialsContractType | null,
  ) => {
    setBillingMethod(billingMethodValue);
  };

  const handleCurrencyChange = (currencyValue: Currency['currencyCode']) => {
    setCurrency(currencyValue);
  };

  return (
    <>
      <BlockList.Item
        disabled={!value || isLoading || isFetching}
        onClick={() => setActionDrawerVisible(true)}
      >
        <BlockList.Item.Label data-required="true">
          <BlockList.Item.Label.Text>
            <FormattedMessage
              id={'settings.integrations.create.automation.project.label'}
            />
          </BlockList.Item.Label.Text>
        </BlockList.Item.Label>
        <BlockList.Item.Content>
          {isLoading || isFetching ? (
            <Loading />
          ) : (
            <RenderBlockDetails
              actions={automationDetails?.[fieldsKey] ?? {}}
              fields={[
                ...projectFields,
                ...(placeholderFields ?? []),
                ...(financialFields ?? []),
              ]}
            />
          )}
        </BlockList.Item.Content>
      </BlockList.Item>
      <ActionDrawer
        label="project"
        fields={projectFields ?? []}
        fieldsStatus={isLoading || isFetching ? 'pending' : 'success'}
        visible={actionDrawerVisible}
        value={automationDetails?.[fieldsKey] ?? undefined}
        handleModalVisibility={setActionDrawerVisible}
        onSave={onSave}
        onClose={() => setActionDrawerVisible(false)}
        placeholderFields={placeholderFields}
        placeholderStatus={placeholderStatus}
        financialFields={financialFields}
        isFinancialsFieldsFetching={isFinancialsFieldsFetching}
        onTemplateChange={handleTemplateChange}
        onBillingMethodChange={handleBillingMethodChange}
        onCurrencyChange={handleCurrencyChange}
      />
    </>
  );
};
